import {
  Autocomplete,
  Button,
  CircularProgress,
  createFilterOptions,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  AutoAwesome,
  FiberManualRecord,
  RemoveRounded,
} from "@mui/icons-material";

import React, { useEffect, useRef, useState } from "react";
import { REPORT_SEVERITY, REPORT_SEVERITY_MAP } from "static/constants";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { cloneDeep } from "lodash";
import { mergeIncomingReportNotes } from "utils/reportTemplate.util";

const ReportTemplatesInput = ({
  reportNotes,
  onUpdate,
  pendingAssociatedNotes = new Set(),
  pendingExtractionResults = [],
  showExtractButton = false,
  canExtractReportNotes = false,
  extractReportNotesTooltip = "",
  handleExtractReportPdfData,
}) => {
  const [inputValue, setInputValue] = useState("");
  const filter = createFilterOptions();
  const theme = useTheme();
  const isFirstRender = useRef(true);
  const isFirstExtractionRender = useRef(true);

  const formik = useFormik({
    initialValues: { reportNotes: reportNotes },
    initialErrors: {},
    validateOnChange: false,
  });

  const { values, setFieldValue } = formik;

  useEffect(() => {
    if (values.reportNotes) {
      onUpdate({ reportNotes: values?.reportNotes });
    }
  }, [values]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!reportNotes) return;

    setFieldValue(
      "reportNotes",
      mergeIncomingReportNotes(values.reportNotes, reportNotes)
    );
  }, [reportNotes]);

  useEffect(() => {
    if (isFirstExtractionRender.current) {
      isFirstExtractionRender.current = false;
      return;
    }
    if (!pendingExtractionResults.length) return;

    const reportNotesList = cloneDeep(values.reportNotes);
    pendingExtractionResults.forEach(
      ({ associatedNote, description, mode }) => {
        reportNotesList.forEach((note) => {
          if (note.associated_note !== associatedNote) return;

          const contents = [...(note.content ?? [])];

          if (mode === "append") {
            const existingIndex = contents.findIndex(
              (item) => item.isExtracted && item.isDefault !== false
            );
            if (existingIndex >= 0) {
              contents[existingIndex] = {
                ...contents[existingIndex],
                description,
              };
            } else {
              contents.unshift({
                description,
                alert: "None",
                isDefault: true,
                isExtracted: true,
              });
            }
          } else {
            const initialIndex = contents.length - 1;
            if (initialIndex >= 0) {
              contents[initialIndex] = {
                ...contents[initialIndex],
                description,
                isDefault: true,
              };
            } else {
              contents.push({ description, alert: "None", isDefault: true });
            }
          }

          note.content = contents;
        });
      }
    );

    setFieldValue("reportNotes", reportNotesList);
  }, [pendingExtractionResults]);

  const handleAddNote = (index) => {
    const reportNotesList = cloneDeep(values.reportNotes);

    const placeholder = reportNotesList[index].placeholders?.[0] || "";
    let newNote = { description: placeholder, alert: "None" };

    const contents = [...(reportNotesList[index]?.content ?? [])];
    contents.unshift(newNote);

    reportNotesList[index].content = contents;

    setFieldValue("reportNotes", reportNotesList);
  };

  const handleSeverityChange = ({ noteAlert, reportNoteIndex, noteIndex }) => {
    const newSeverityIndex =
      (REPORT_SEVERITY.indexOf(noteAlert) + 1) % REPORT_SEVERITY.length;

    setFieldValue(
      `reportNotes[${reportNoteIndex}].content[${noteIndex}].alert`,
      REPORT_SEVERITY[newSeverityIndex]
    );
  };

  return (
    <>
      <FormikProvider value={formik}>
        <form style={{ marginTop: "16px" }}>
          <div className="d-flex justify-between align-items-center">
            <h2 className="mb-0" data-label="report-notes-header">
              Report Notes
            </h2>
            {showExtractButton && (
              <Tooltip title={extractReportNotesTooltip}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    aria-label="extract report data from pdf"
                    data-action="report-notes-extract-pdf-data-button"
                    disabled={!canExtractReportNotes}
                    onClick={handleExtractReportPdfData}
                    startIcon={
                      pendingAssociatedNotes.size > 0 ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AutoAwesome />
                      )
                    }
                  >
                    Analyze PDF
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
          <List>
            {values.reportNotes?.map((reportNote, reportNoteIndex) => {
              return (
                <List
                  style={{
                    padding: "2px 0",
                    marginBottom: "10px",
                  }}
                  key={reportNoteIndex}
                >
                  <ListItem
                    disableGutters
                    className="report-note-item-manual-upload"
                    style={{
                      height: "36px",
                      backgroundColor:
                        theme.palette.type === "dark" ? "#333333" : "#f5f5f5",
                      borderColor:
                        theme.palette.type === "dark" ? "#444444" : "#E0E0E0",
                    }}
                  >
                    <ListItemText
                      primary={reportNote?.note}
                      primaryTypographyProps={{ style: { fontWeight: 500 } }}
                    />
                    <Tooltip title="Add new note">
                      <ListItemSecondaryAction
                        onClick={() => handleAddNote(reportNoteIndex)}
                        data-action={`report-notes-add-${reportNote?.note}-button`}
                      >
                        <IconButton aria-label="add" sx={{ padding: "4px" }}>
                          <AddIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </Tooltip>
                  </ListItem>

                  <FieldArray
                    name={`reportNotes[${reportNoteIndex}].content`}
                    validateOnChange={false}
                    render={(arrayHelpers) => (
                      <Grid container spacing={1}>
                        {reportNote?.content.map((content, noteIndex) => {
                          const isPending =
                            noteIndex === reportNote.content.length - 1 &&
                            content?.isDefault !== false &&
                            reportNote?.associated_note &&
                            pendingAssociatedNotes.has(
                              reportNote.associated_note
                            );

                          return (
                            <React.Fragment key={noteIndex}>
                              <Grid item xs={11}>
                                {isPending ? (
                                  <TextField
                                    fullWidth
                                    disabled
                                    multiline
                                    maxRows={3}
                                    size="small"
                                    variant="outlined"
                                    className="reportNote"
                                    placeholder={`Scanning PDF for ${reportNote.associated_note}...`}
                                    data-text={`report-notes-${reportNote?.note}-description-${noteIndex}-loading`}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <CircularProgress size={20} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                ) : (
                                  <Autocomplete
                                    freeSolo
                                    id={`reportNotes-${reportNoteIndex}-content-${noteIndex}`}
                                    name={`reportNotes-${reportNoteIndex}-content-${noteIndex}`}
                                    data-action={`report-notes-${reportNote?.note}-autocomplete-${noteIndex}`}
                                    openOnFocus
                                    value={content?.description || null}
                                    options={
                                      reportNote?.placeholders?.filter(
                                        Boolean
                                      ) || []
                                    }
                                    getOptionLabel={(option) => option}
                                    onInputChange={(
                                      event,
                                      newValue,
                                      reason
                                    ) => {
                                      if (reason !== "input") return;

                                      const value = newValue?.trim() || "";

                                      setInputValue(value);
                                      setFieldValue(
                                        `reportNotes[${reportNoteIndex}].content[${noteIndex}].description`,
                                        value
                                      );
                                      setFieldValue(
                                        `reportNotes[${reportNoteIndex}].content[${noteIndex}].isDefault`,
                                        false
                                      );
                                    }}
                                    onChange={(event, newValue) => {
                                      const formattedNewValue = (
                                        reportNote?.placeholders || []
                                      ).includes(newValue)
                                        ? newValue
                                        : newValue?.trim() || "";
                                      setFieldValue(
                                        `reportNotes[${reportNoteIndex}].content[${noteIndex}].description`,
                                        formattedNewValue
                                      );
                                      setFieldValue(
                                        `reportNotes[${reportNoteIndex}].content[${noteIndex}].isDefault`,
                                        false
                                      );
                                    }}
                                    filterOptions={(options, params) => {
                                      const filtered = filter(options, {
                                        ...params,
                                        inputValue,
                                      });

                                      return filtered;
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        multiline
                                        maxRows={3}
                                        size="small"
                                        id="reportNote"
                                        variant="outlined"
                                        className="reportNote"
                                        data-text={`report-notes-${reportNote?.note}-description-${noteIndex}-field`}
                                        inputProps={{
                                          ...params?.inputProps,
                                          spellCheck: true,
                                        }}
                                      />
                                    )}
                                  />
                                )}
                              </Grid>

                              <Grid
                                item
                                xs={1}
                                className="d-flex align-items-center align-start"
                              >
                                <Tooltip title={`Severity: ${content.alert} `}>
                                  <FiberManualRecord
                                    value={content.alert}
                                    onClick={() =>
                                      handleSeverityChange({
                                        noteAlert: content.alert || "None",
                                        reportNoteIndex,
                                        noteIndex,
                                      })
                                    }
                                    data-action={`report-notes-${reportNote?.note}-severity-${noteIndex}-button`}
                                    style={{
                                      color:
                                        REPORT_SEVERITY_MAP[
                                          content.alert || "None"
                                        ],
                                      cursor: "pointer",
                                      fontSize: "26px",
                                    }}
                                  />
                                </Tooltip>

                                <Tooltip title={`Remove Note`}>
                                  <RemoveRounded
                                    onClick={() =>
                                      arrayHelpers.remove(noteIndex)
                                    }
                                    data-action={`report-notes-${reportNote?.note}-remove-${noteIndex}-button`}
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "30px",
                                    }}
                                  />
                                </Tooltip>
                              </Grid>
                            </React.Fragment>
                          );
                        })}
                      </Grid>
                    )}
                  />
                </List>
              );
            })}
          </List>
        </form>
      </FormikProvider>
    </>
  );
};

export default ReportTemplatesInput;
